import { useEffect, useMemo, useState } from 'react'
import { LIST_EXPORT_SETTINGS } from '../data/listExportSettings'
import { getPreference, savePreference } from '../api/preferences'

type Props = {
  open: boolean
  onClose: () => void
}

export function ExportSettingsModal({ open, onClose }: Props) {
  const rows = LIST_EXPORT_SETTINGS.rows

  const initialChecked = useMemo(() => {
    const map: Record<string, boolean> = {}
    for (const r of rows) map[r.value] = r.checked
    return map
  }, [])

  const initialPriority = useMemo(() => {
    const map: Record<string, string> = {}
    for (const r of rows) map[r.value] = r.priority
    return map
  }, [])

  const [checked, setChecked] = useState(initialChecked)
  const [priority, setPriority] = useState(initialPriority)
  const [msg, setMsg] = useState('')

  useEffect(() => {
    if (!open) return
    void getPreference<{ checked: Record<string, boolean>; priority: Record<string, string> }>('clients.export')
      .then((saved) => {
        if (!saved) return
        setChecked(saved.checked)
        setPriority(saved.priority)
      })
  }, [open])

  if (!open) return null

  const selectedCount = Object.values(checked).filter(Boolean).length

  const onSave = async () => {
    if (selectedCount === 0) {
      setMsg('Please select at least one export column.')
      return
    }
    for (const r of rows) {
      if (!checked[r.value]) continue
      const p = Number(priority[r.value])
      if (!priority[r.value]) {
        setMsg('Priority can not be empty.')
        return
      }
      if (!(p > 0)) {
        setMsg('Priority can not be zero or less than zero.')
        return
      }
    }
    await savePreference('clients.export', { checked, priority })
    setMsg('Export Settings updated successfully.')
    setTimeout(() => {
      setMsg('')
      onClose()
    }, 700)
  }

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="modal-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="export-settings-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-panel__head">
          <h2 id="export-settings-title">Client Export Settings</h2>
          <button type="button" className="icon-btn" aria-label="Close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="modal-panel__body">
          <section className="fs-print-section">
            <h3>Export Column</h3>
            <table className="fs-print-table">
              <thead>
                <tr>
                  <th />
                  <th>Export column name</th>
                  <th>Priority</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.value}>
                    <td>
                      <input
                        type="checkbox"
                        checked={!!checked[r.value]}
                        onChange={(e) => {
                          const on = (e.target as HTMLInputElement).checked
                          setChecked((prev) => ({ ...prev, [r.value]: on }))
                        }}
                      />
                    </td>
                    <td>{r.label}</td>
                    <td>
                      <input
                        className="fs-priority-input"
                        type="text"
                        value={priority[r.value] || ''}
                        disabled={!!r.priorityDisabled && !checked[r.value]}
                        onChange={(e) =>
                          setPriority((prev) => ({
                            ...prev,
                            [r.value]: (e.target as HTMLInputElement).value,
                          }))
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </div>

        <div className="modal-panel__foot">
          {msg ? (
            <span className="muted">{msg}</span>
          ) : (
            <span className="muted">{selectedCount} columns selected</span>
          )}
          <div style={{ flex: '1' }} />
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn--primary" onClick={() => void onSave()}>
            Save
          </button>
        </div>
      </div>
    </div>
  )
}
